import { Sparkles, CalendarDays, Gauge, Compass, Briefcase, Wallet } from "lucide-react";
import { fromKm, fmt, unitLabel } from "@/lib/units";
import { fmtMoney } from "@/lib/currency";
import { colorForVehicle } from "@/lib/vehicleColors";

const ICONS = {
  avg_trip: Sparkles,
  weekend: CalendarDays,
  weekday: CalendarDays,
  limit_pace: Gauge,
  longest: Compass,
  business_split: Briefcase,
  deductible: Wallet,
};

const SENTIMENT_STYLES = {
  good: { bg: "bg-[#16A34A]/12", border: "border-[#16A34A]/30", icon: "text-[#34D17A]" },
  warn: { bg: "bg-[#E82127]/12", border: "border-[#E82127]/30", icon: "text-[#FF5A5F]" },
  neutral: { bg: "bg-[var(--bg-inverse)]/5", border: "border-white/10", icon: "text-[var(--text-primary)]" },
};

function buildOperationalInsights({ stats, unit, currencySymbol, deductibleRate }) {
  if (!stats) return [];
  const u = unitLabel(unit);
  const out = [];
  const personalKm = stats.personal_km || 0;
  const businessKm = stats.business_km || 0;
  const totalSplit = personalKm + businessKm;

  if (totalSplit > 0 && businessKm > 0) {
    const bizPct = (businessKm / totalSplit) * 100;
    out.push({
      kind: "business_split",
      sentiment: "neutral",
      title: `${fmt(fromKm(businessKm, unit), 0)} ${u} business · ${fmt(fromKm(personalKm, unit), 0)} ${u} personal`,
      sub: `${bizPct.toFixed(0)}% of distance is business use`,
    });
  }

  if (businessKm > 0 && deductibleRate > 0) {
    const deductible = businessKm * deductibleRate;
    out.push({
      kind: "deductible",
      sentiment: "good",
      title: `${fmtMoney(deductible, currencySymbol, 0)} estimated deduction`,
      sub: `${fmt(fromKm(businessKm, unit), 0)} ${u} × ${fmtMoney(deductibleRate, currencySymbol, 2)}/km`,
    });
  }

  return out;
}

function VehicleBadge({ vehicle, vehicles }) {
  if (!vehicle) return null;
  const c = colorForVehicle(vehicle.id, vehicles);
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-mono-data border shrink-0"
      style={{
        background: c.soft,
        borderColor: c.border,
        color: c.text,
        borderRadius: 2,
      }}
      data-testid={`insight-vehicle-badge-${vehicle.id}`}
      title={vehicle.name}
    >
      <span
        className="inline-block w-1.5 h-1.5"
        style={{ background: c.fill, borderRadius: 1 }}
      />
      {vehicle.name}
    </span>
  );
}

export default function InsightsPanel({
  insights,
  stats,
  unit = "km",
  currencySymbol = "$",
  deductibleRate = 0.7,
  isMultiAll = false,
  morphCapable = false,
  perVehicleInsights = [],
  vehicles = [],
}) {
  const multi = isMultiAll && vehicles.length > 1;
  const operational = buildOperationalInsights({ stats, unit, currencySymbol, deductibleRate });

  // In multi mode, the activity feed is an aggregated chronological-feeling
  // list of per-vehicle insights, each tagged with a vehicle badge. Operational
  // insights (business split, deductible) are fleet-level so they stay on top.
  const combined = multi
    ? [...operational, ...perVehicleInsights]
    : [...operational, ...(insights || [])];

  // Pin a stable min-height when the household is morph-capable (>1 vehicle)
  // so the panel doesn't shrink when the user filters to one vehicle.
  const stableStyle = morphCapable
    ? { borderRadius: 4, minHeight: 520 }
    : { borderRadius: 4 };

  return (
    <div
      className="bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-5 h-full flex flex-col card-hover"
      style={stableStyle}
      data-testid="insights-panel"
      data-mode={multi ? "multi" : "single"}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-medium text-[var(--text-primary)]">
          {multi ? "Activity Feed" : "Insights"}
        </h3>
        <span className="label-tiny">{multi ? "Per Vehicle" : "Auto"}</span>
      </div>

      {combined.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-[var(--text-secondary)] text-sm">
          Log a few more trips to unlock insights.
        </div>
      ) : (
        <div className="space-y-3">
          {combined.map((it, i) => {
            const Icon = ICONS[it.kind] || Sparkles;
            const s = SENTIMENT_STYLES[it.sentiment] || SENTIMENT_STYLES.neutral;
            const vehicle = it.vehicleId ? vehicles.find((v) => v.id === it.vehicleId) : null;
            return (
              <div
                key={`${it.kind}-${it.vehicleId || "agg"}-${i}`}
                className="flex items-start gap-3 group"
                data-testid={`insight-${it.kind}`}
              >
                <div
                  className={`shrink-0 w-9 h-9 flex items-center justify-center ${s.bg} border ${s.border} transition-transform duration-200 group-hover:scale-105`}
                  style={{ borderRadius: 999 }}
                >
                  <Icon className={`w-4 h-4 ${s.icon}`} strokeWidth={1.75} />
                </div>
                <div className="min-w-0 pt-0.5 flex-1">
                  <div className="flex items-start gap-2 flex-wrap">
                    <div className="text-sm text-[var(--text-primary)] leading-tight tracking-tight">
                      {it.title}
                    </div>
                    {vehicle && <VehicleBadge vehicle={vehicle} vehicles={vehicles} />}
                  </div>
                  <div className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-snug">{it.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
